import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { ListSkeleton } from "@/components/PageSkeleton";
import { listConversations, getConversation } from "@/lib/conversations.functions";
import { supabase } from "@/integrations/supabase/client";
import { MessageSquare } from "lucide-react";
import { useHydratedTranslation } from "@/components/marketing/Layout";

import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/_authenticated/conversations")({
  head: () =>
    buildSeo({
      path: "/conversations",
      title: "Conversations — Reality Twin",
      description:
        "Review every conversation your AI Twin has held with visitors — inspect transcripts, follow-ups and lead outcomes in real time.",
      noindex: true,
    }),
  component: Page,
});

function Page() {
  const t = useHydratedTranslation();
  const fetchList = useServerFn(listConversations);
  const fetchOne = useServerFn(getConversation);
  const [convs, setConvs] = useState<any[]>([]);
  const [active, setActive] = useState<any>(null);
  const [msgs, setMsgs] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchList()
      .then((r) => setConvs(r.conversations))
      .finally(() => setLoading(false));
  }, [fetchList]);

  useEffect(() => {
    const channel = supabase
      .channel("conversations-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "conversations" }, () => {
        fetchList().then((r) => setConvs(r.conversations));
      })
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        (payload: any) => {
          if (active && payload.new?.conversation_id === active.id) {
            setMsgs((m) => [...m, payload.new]);
          }
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchList, active]);

  async function open(id: string) {
    const r = await fetchOne({ data: { id } });
    setActive(r.conversation);
    setMsgs(r.messages);
  }

  return (
    <DashboardShell
      title={t("app.pages.conversations.title")}
      subtitle={t("app.pages.conversations.subtitle")}
    >
      <div className="grid lg:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-200px)] min-h-[500px]">
        <div className="glass-strong rounded-2xl overflow-y-auto flex flex-col">
          <div className="p-3 border-b border-border shrink-0">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("app.pages.conversations.searchPh")}
              className="w-full rounded-full bg-white/5 border border-border px-3.5 py-1.5 text-xs focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
            />
          </div>
          {loading ? (
            <ListSkeleton rows={5} className="rounded-none border-0" />
          ) : convs.filter(
              (c) =>
                !q.trim() ||
                [c.title, c.visitor_name, c.visitor_email].some((v) =>
                  (v ?? "").toLowerCase().includes(q.trim().toLowerCase()),
                ),
            ).length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {q ? t("app.pages.conversations.noMatch") : t("app.pages.conversations.empty")}
            </div>
          ) : (
            convs
              .filter(
                (c) =>
                  !q.trim() ||
                  [c.title, c.visitor_name, c.visitor_email].some((v) =>
                    (v ?? "").toLowerCase().includes(q.trim().toLowerCase()),
                  ),
              )
              .map((c) => (
                <button
                  key={c.id}
                  onClick={() => open(c.id)}
                  className={`w-full text-left p-4 border-b border-border hover:bg-white/5 ${active?.id === c.id ? "bg-white/5" : ""}`}
                >
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-[oklch(0.82_0.18_168)]" />
                    {c.source === "visitor"
                      ? t("app.pages.conversations.visitor")
                      : t("app.pages.conversations.owner")}{" "}
                    · {new Date(c.created_at).toLocaleDateString()}
                  </div>
                  <div className="text-sm font-medium line-clamp-2">
                    {c.title ?? t("app.pages.conversations.conversation")}
                  </div>
                  {c.visitor_email && (
                    <div className="text-xs text-muted-foreground mt-1">{c.visitor_email}</div>
                  )}
                </button>
              ))
          )}
        </div>
        <div className="glass-strong rounded-2xl p-6 overflow-y-auto">
          {!active ? (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-sm">
              <MessageSquare className="h-8 w-8 mb-2 opacity-40" />
              {t("app.pages.conversations.selectPrompt")}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="pb-3 border-b border-border">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                  {t("app.pages.conversations.conversation")}
                </div>
                <div className="font-display text-lg mt-1">
                  {active.title ?? t("app.pages.conversations.conversation")}
                </div>
              </div>
              {msgs.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "brand-gradient text-black" : "glass"}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
